import * as React from 'react';
import { I18n } from "react-redux-i18n";
import { CustomPanelProps } from "./custom-panel.types";
import { getTestAttributes } from '@common/utils/tests';
import { LabListDto } from '@common/dto/lab/lab-list-dto';
import { useMessage } from '@common/hooks/message';
import { OTHER_LAB_OPTION_NAME } from '@modules/testResults/const/TestResultsConst';
import { PanelFolderListDto } from '@modules/testResults/dto/panel-folder-list-dto';
import { FolderPreviewManagementFormat } from '@common/components/folder-preview-management/folder-preview-management.types';
import { PanelCreateDto } from '@modules/testResults/dto/panel/create-dto';
import { HttpErrorDto } from '@common/dto/http';
import { PanelUpdateDto } from '@modules/testResults/dto/panel/update-dto';
import { InfoMessage } from '@common/components/info-message';
import { FormControl } from '@common/components/form-control';

export const useController = (props: CustomPanelProps) => {
  const { 
    mode,
    getLabs,
    genes,
    getFolders,
    getPanels,
    createFolder,
    deletePanelFolder,
    deletePanel,
    onCreate,
    onUpdate,
    getPanelFormat,
    onGenesLoad,
    doesPanelNameExist,
    onOverwrite 
  } = props;
  const [panelMetadata, setPanelMetadata] = React.useState<{ folder: { id: number }; format: { id?: number; name: string } }>();
  const [testDetailsOpened, setTestDetailsOpened] = React.useState(false);
  const [saveAsFormatManagementOpened, setSaveAsFormatManagementOpened] = React.useState(false);
  const [customTestName, setCustomTestName] = React.useState('');
  const [labName, setLabName] = React.useState('');
  const [otherLabName, setOtherLabName] = React.useState('');

  const [panelFormatId, setPanelFormatId] = React.useState<number>();
  
  const {
    showConfirmMessage,
    showErrorMessage,
    messageProps,
    closeMessage,
  } = useMessage();
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [labs, setLabs] = React.useState<Array<LabListDto>>([]);
  const [folders, setFolders] = React.useState<Array<{ id: number; name: string; }>>([]);
  const [formats, setFormats] = React.useState<Array<FolderPreviewManagementFormat>>([]);
  const [overwritePanel, setOverwritePanel] = React.useState(false);

  React.useEffect(() => {
    getLabs()
      .then((res) => {
        setLabs([
          ...res,
          {
            name: OTHER_LAB_OPTION_NAME,
            tests: [],
          } as LabListDto
        ]);
      })
      .catch((err) => {
        console.error(err);
        showErrorMessage(I18n.t('Failed to get labs'))
      });
  }, []);

  type Config = {
    formatManagement: {
      title: string;
      submitBtnProps?: {
        text?: string;
      };
    };
    preview: {
      title: string;
      submitBtnProps?: {
        title?: string
      }
    };
  };

  const config: Partial<Record<typeof mode, Config>> = {
    create: {
      formatManagement: {
        title: I18n.t('New Custom Panel'),
        submitBtnProps: {
          text: I18n.t('OK')
        },
      },
      preview: {
        title: I18n.t('Add New Custom Panel')
      },
    },
    edit: {
      formatManagement: {
        title: I18n.t('Load Custom Panel'),
        submitBtnProps: {
          text: saveAsFormatManagementOpened ? I18n.t('OK') : undefined
        }
      },
      preview: {
        title: I18n.t('Load Custom Panel'),
        submitBtnProps: {
          title: I18n.t('Load')
        }
      }
    },
  };
  const currentConfig = config[mode];

  const handleGetPanelFormat = React.useCallback(async (panelId: number) => {
    getPanelFormat(panelId)
      .then((res) => {
        setLabName(res.lab);
        setOtherLabName(res.otherlab);
        setCustomTestName(res.customname);
        setPanelFormatId(res.id);
        
        onGenesLoad(res.genes.map(({ priorclassification, ...otherData }) => ({ ...otherData, priorClassification: priorclassification})));
      })
      .catch(err => {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to get panel format'))
      });
  }, [getPanelFormat, showErrorMessage, onGenesLoad]);

  const handleOpenTestDetails = React.useCallback((data: { folder: { id: number }, format: { id?: number; name: string } }) => {
    setTestDetailsOpened(true);
    setPanelMetadata(data);
  }, []);

  const handleFolderPreviewSubmit = async (data: { folder: { id: number }, format: { id?: number; name: string } }) => {
    if (mode === 'edit' && data.format?.id) {
      await handleGetPanelFormat(data.format.id).then(() => {
        handleOpenTestDetails(data);
      });
    } else {
      try {
        const exists = await doesPanelNameExist(data.folder.id, data.format.name);

        if (exists) {
          showConfirmMessage({
            cancelText: I18n.t('Cancel'),
            confirmText: I18n.t('Overwrite'),
            content: <InfoMessage>{I18n.t('existingCustomPanel')}</InfoMessage>,
            onCustomOk: () => {
              setOverwritePanel(true);
              handleOpenTestDetails(data)
            },
          });
        } else {
          handleOpenTestDetails(data);
        }
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to check panel name'))
      }
    }
  }

  const handleCreatePanel = async (panel: PanelCreateDto) => {
    try {
      setSubmitting(true);
      await onCreate(panel);
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to create panel'))
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverwritePanel = async (panel: PanelCreateDto) => {
    try {
      setSubmitting(true);
      await onOverwrite(panel);
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to create panel'))
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePanel = async (panel: PanelUpdateDto) => {
    try {
      setSubmitting(true);
      await onUpdate(panel);
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to update panel'))
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestSubmit = async () => {
    if (!panelMetadata) {
      return;
    }

    const commonPayload: PanelCreateDto = {
      folderid: panelMetadata.folder.id,
      formatname: panelMetadata.format?.name ?? "",
      lab: labName,
      otherlab: otherLabName,
      customname: customTestName,
      genes: genes.map((x) => ({
        gene: x.gene,
        variant: x.variant,
        classification: x.classification,
        priorclassification: x.priorClassification,
      })),
    };

    if (mode === "create") {
      if (overwritePanel) {
        await handleOverwritePanel(commonPayload)
      } else {
        await handleCreatePanel(commonPayload);
      }
    } else {
      await handleUpdatePanel({
        ...commonPayload,
        id: panelFormatId!,
      });
    }
  };

  const closeTestDetails = React.useCallback(() => {
    setTestDetailsOpened(false);
  }, []);

  const handleCustomTestNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTestName(e.target.value);
  }, []);

  const renderCustomTestName = React.useCallback((fieldBefore: string) => {
    if (fieldBefore === 'test') {
      return (
        <div className="row" style={{marginBottom: '5px'}}>
          <div 
            {...getTestAttributes('TestResults', 'custom-test-name', 'label')} 
            className="col-sm-4 CustomPanel__custom-name-label" 
          >
            {I18n.t('Custom Test Name')}
          </div>
          <div className="col-sm-8">
            <FormControl 
              {...getTestAttributes('TestResults', 'otherTestOrdered', 'input')}
              value={customTestName}
              className="CustomPanel__custom-name-field"
              placeholder={I18n.t('Custom Test Name')}
              onChange={handleCustomTestNameChange}
            />
          </div>
        </div>
      );
    }
  }, [customTestName, handleCustomTestNameChange]);

  const handleLabChange = React.useCallback((labName: string) => {
    setLabName(labName);

    if (OTHER_LAB_OPTION_NAME !== labName) {
      setOtherLabName('');
    }
  }, []);

  const handleOtherLabChange = React.useCallback((fieldName: string, value: string) => {
    setOtherLabName(value);
  }, []);

  type ProcessedFolder = {
    id: number;
    name: string;
    parentid: number;
    children?: Array<ProcessedFolder>;
  };

  const getProcessedFolders = React.useCallback(async (): Promise<Array<ProcessedFolder>> => {
    const folders = await getFolders();

    const processFolder = (folder: PanelFolderListDto): ProcessedFolder => {
      return {
        id: folder.id,
        parentid: folder.parentid,
        name: folder.foldername,
        children: folder.children?.map(processFolder)
      };
    };

    const processedFolders = folders.map(processFolder);

    return processedFolders;
  }, [getFolders]);

  const getAndSetFolders = React.useCallback(async () => {
    const res = await getProcessedFolders();
    setFolders(res);
  }, [getProcessedFolders]);

  const getAndSetFormats = React.useCallback(async (folderId: number) => {
    const res = await getPanels(folderId);
    setFormats(res);
  }, [getPanels]);

  const resetFormats = React.useCallback(() => {
    setFormats([]);
  }, []);

  const closeSaveAsFormatManagement = React.useCallback(() => {
    setSaveAsFormatManagementOpened(false);
  }, []);

  const handleFolderCreate = React.useCallback(async (data: { parentId?: number; name: string }) => {
    await createFolder({
      parentid: data.parentId,
      foldername: data.name, 
    });
    await getAndSetFolders();
  }, [createFolder, getAndSetFolders]);

  const handleFolderDelete = React.useCallback(async (folderId: number) => {
    await deletePanelFolder(folderId);
    await getAndSetFolders();
  }, [deletePanelFolder, getAndSetFolders]);

  const handlePanelDelete = React.useCallback(async (folderId: number, formatId: number) => {
    await deletePanel(formatId);
    await getAndSetFormats(folderId);
  }, [deletePanel, getAndSetFolders]);

  const handleSaveAsClick = React.useCallback(() => {
    setSaveAsFormatManagementOpened(true);
  }, []);

  const handleChooseCustomPanelForSaveAs = async (
    { folderId, name: newFormatName }: { folderId: number; name?: string }, 
    { format }: { format?: { id: number; name: string } }
  ) => {
    if (format) {
      const payload: PanelUpdateDto = {
        id: format.id,
        folderid: folderId,
        formatname: format.name,
        lab: labName,
        otherlab: otherLabName,
        customname: customTestName,
        genes: genes.map((x) => ({
          gene: x.gene,
          variant: x.variant,
          classification: x.classification,
          priorclassification: x.priorClassification,
        })),
      };
  
      await handleUpdatePanel(payload);

      return;
    }

    if (newFormatName) {
      const payload: PanelCreateDto = {
        folderid: folderId,
        formatname: newFormatName,
        lab: labName,
        otherlab: otherLabName,
        customname: customTestName,
        genes: genes.map((x) => ({
          gene: x.gene,
          variant: x.variant,
          classification: x.classification,
          priorclassification: x.priorClassification,
        })),
      };

      try {
        const exists = await doesPanelNameExist(folderId, newFormatName);

        if (exists) {
          showConfirmMessage({
            cancelText: I18n.t('Cancel'),
            confirmText: I18n.t('Overwrite'),
            content: <InfoMessage>{I18n.t('existingCustomPanel')}</InfoMessage>,
            onCustomOk: () => {
              handleOverwritePanel(payload)
            },
          });
        } else {
          handleCreatePanel(payload);
        }
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to check panel name'))
      }
    }
  };

  const testDetailsErrors = React.useMemo(() => {
    return {
      lab: labName ? undefined : I18n.t('The field is required'),
      otherLab: OTHER_LAB_OPTION_NAME === labName && !otherLabName
        ? I18n.t('The field is required')
        : undefined,
      customTestName: customTestName ? undefined : I18n.t('The field is required'),
    };
  }, [labName, otherLabName, customTestName]);

  const testDetailsHasErrors = React.useCallback(() => {
    return Object.values(testDetailsErrors).some((x) => Boolean(x));
  }, [testDetailsErrors]);

  return {
    testDetailsHasErrors,
    submitting,
    handleChooseCustomPanelForSaveAs,
    handleFolderCreate,
    handleFolderDelete,
    handlePanelDelete,
    getAndSetFolders,
    getAndSetFormats,
    resetFormats,
    folders,
    formats,
    handleSaveAsClick,
    saveAsFormatManagementOpened,
    closeSaveAsFormatManagement,
    getProcessedFolders,
    handleTestSubmit,
    labName,
    otherLabName,
    handleOtherLabChange,
    handleLabChange,
    labs,
    messageProps,
    closeMessage,
    renderCustomTestName,
    closeTestDetails,
    testDetailsOpened,
    currentConfig,
    handleFolderPreviewSubmit,
  };
};